import { useCallback, useEffect, useRef, useState } from "react";
import {
  MAXES_CACHE_TTL_MS,
  accountScope,
  errorCodeOf,
  datedMaxes,
  fetchMaxesDetailed,
  type AccountScope,
  type ApiFetcher,
  type CurrentMax,
} from "@heraion/shared";

import type { ResourceCache } from "../Logger/state/asyncListCache";
import { useRetryOnReconnect } from "../useRetryOnReconnect";

// Shared MAX data layer — TTL cache + in-flight coalescing + the
// setAt-keeping `maxMeta` map — lifted verbatim from apps/mobile's
// MaxesContext so the mobile Provider and the web-rnw Maxes console read
// ONE implementation. Mobile keeps wrapping this in a Context (the
// post-save MAX-bump pill + plan runner consume the singleton); web calls
// the hook directly.
//
// Storage shape mirrors the API: { exerciseId → kg }, kept canonical (kg)
// per Profile.unitsPreference's "convert at the edge" model — consumers
// format for display. `maxMeta` keeps the same data plus `setAt` (the MAX-
// regression staleness gate needs it), derived from the same single fetch.
//
// `loaded` stays false until the first SUCCESSFUL fetch so the MAX-bump
// check can skip evaluation against an empty map (else every weighted set
// reads as a new MAX). On fetch failure the previously-stored map is kept
// (covers a transient blip after a prior success). apiFetch goes through a
// ref so a fresh literal per render never re-kicks the boot fetch.
/** "failed" only until a read succeeds: once the MAXes are known, a later
 *  failure (offline) keeps serving them as loaded. */
export type MaxesStatus = "loading" | "loaded" | "failed";

export type MaxesData = {
  maxes: Map<string, number>;
  maxMeta: Map<string, CurrentMax>;
  loaded: boolean;
  status: MaxesStatus;
  /** The last read's failure code (`api_offline`, `api_error_500`...), null
   *  once a read succeeds: what "failed" says, offline or a fault (#397). */
  error: string | null;
  /** Returns a fresh-or-cached map; dedupes concurrent calls on the same
   *  in-flight promise. Use for the read path. */
  getFresh: () => Promise<Map<string, number>>;
  /** The same read as `getFresh`, but handing back the DETAILED map — value
   *  plus `setAt` plus `source`. Shares the TTL and the in-flight promise, so
   *  it costs no extra request; it exists because reading `maxMeta` off the
   *  render closure right after awaiting `getFresh` can hand back the PREVIOUS
   *  fetch (state has not re-rendered yet). A consumer that needs the source
   *  to decide whether it may propose a load (resolveDefaultWeight's effort
   *  arm, PG-9) would then silently see "unknown" and decline. */
  getFreshDetailed: () => Promise<Map<string, CurrentMax>>;
  /** Bypasses the TTL. Use after a mutation (PUT/DELETE) so the next read
   *  sees the new value without waiting for MAXES_CACHE_TTL_MS. */
  forceRefresh: () => Promise<Map<string, number>>;
};

/** #397: the copy on disk holds at most this many MAXes; past it the copy is
 *  deleted, not cut (a MAX missing from a copy would read as "Not set") nor
 *  left behind (offline would show the older MAXes it holds). */
export const MAXES_DISK_ROWS = 400;

type MaxesDiskCopy = ResourceCache<Array<[string, CurrentMax]>> & {
  clear: (scope: AccountScope) => Promise<void>;
};

export type UseMaxesDataOptions = {
  apiFetch: ApiFetcher;
  /** #397: the athlete's own disk copy, read at mount for offline. Its MAXes
   *  show and seed loads, but `loaded` waits for the server: a MAX-bump is
   *  never judged against a copy. */
  cache?: MaxesDiskCopy;
  /** #469: the writes still queued, laid over what the server (or the disk
   *  copy) said, so a MAX corrected offline shows and seeds loads. Gets the
   *  server's map and must not change it: the disk copy is written from it.
   *  Mobile only; web writes straight through and has no queue. */
  overlay?: (server: Map<string, CurrentMax>) => Map<string, CurrentMax>;
  /** #469: told when the queue under `overlay` changes; returns the
   *  unsubscribe. `settled` is true when the queue got shorter: a write
   *  reached the server, which is read again so its value does not flip back
   *  to the old one when the overlay lets go of it. */
  subscribe?: (onChange: (settled: boolean) => void) => () => void;
};

export function useMaxesData(opts: UseMaxesDataOptions): MaxesData {
  const [maxes, setMaxes] = useState<Map<string, number>>(new Map());
  const [maxMeta, setMaxMeta] = useState<Map<string, CurrentMax>>(new Map());
  // The detailed map kept OUT of React state too, so an awaited read sees the
  // fetch that just landed rather than the closure's stale render value.
  const metaRef = useRef<Map<string, CurrentMax>>(new Map());
  const [status, setStatus] = useState<MaxesStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const lastFetchedRef = useRef<number>(0);
  const inFlightRef = useRef<Promise<Map<string, number>> | null>(null);

  // #469: the last map the server (or its disk copy) gave, before the
  // overlay. Null until one arrives: a queued write alone paints nothing.
  const serverRef = useRef<Map<string, CurrentMax> | null>(null);

  const apiRef = useRef(opts.apiFetch);
  const cacheRef = useRef(opts.cache);
  const overlayRef = useRef(opts.overlay);
  useEffect(() => {
    apiRef.current = opts.apiFetch;
    cacheRef.current = opts.cache;
    overlayRef.current = opts.overlay;
  });

  const store = useCallback((server: Map<string, CurrentMax>) => {
    serverRef.current = server;
    const overlay = overlayRef.current;
    const detailed = overlay ? overlay(server) : server;
    // Derive the canonical kg-only map from the same read so existing
    // consumers (MAX-bump pill, plan runner) are untouched.
    const next = new Map<string, number>();
    for (const [slug, m] of detailed) next.set(slug, m.valueKg);
    setMaxes(next);
    setMaxMeta(detailed);
    metaRef.current = detailed;
    return next;
  }, []);

  const fetchAndStore = useCallback(async (): Promise<Map<string, number>> => {
    if (inFlightRef.current) return inFlightRef.current;
    // #398: the account this read is for; the disk copy is written (or
    // cleared) under it, and refuses once it has changed.
    const scope = accountScope();
    const promise = (async () => {
      try {
        const detailed = datedMaxes(await fetchMaxesDetailed(apiRef.current));
        const next = store(detailed);
        lastFetchedRef.current = Date.now();
        setStatus("loaded");
        setError(null);
        // ⛔ The server's map, never the overlaid one (#469): a queued write
        // on disk would outlive its own rejection.
        const disk = cacheRef.current;
        if (disk) {
          void (detailed.size <= MAXES_DISK_ROWS
            ? disk.write([...detailed], scope)
            : disk.clear(scope));
        }
        return next;
      } catch (err) {
        // Best-effort: leaving `loaded` false signals the consumer to skip
        // MAX-bump evaluation. The map already in state stays.
        setStatus((prev) => (prev === "loaded" ? "loaded" : "failed"));
        setError(errorCodeOf(err));
        return maxes;
      } finally {
        inFlightRef.current = null;
      }
    })();
    inFlightRef.current = promise;
    return promise;
  }, [maxes, store]);

  // Fire one fetch on mount. Mobile mounts the Provider inside TabShell
  // (post Cognito sign-in) so apiFetch has a token; web mounts the hook
  // inside the authed AppShell.
  useEffect(() => {
    let cancelled = false;
    // The disk copy paints first, unless the server answered before it.
    void cacheRef.current?.read().then((kept) => {
      if (!cancelled && kept !== null && lastFetchedRef.current === 0) store(new Map(kept));
    });
    void fetchAndStore();
    return () => {
      cancelled = true;
    };
    // Empty deps on purpose: only the boot fetch here. Later refreshes flow
    // through getFresh / forceRefresh, and the reconnect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // #397: never read (offline at mount), read again when the network is back.
  const retry = useRetryOnReconnect(status === "failed");
  useEffect(() => {
    if (retry > 0) void fetchAndStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retry]);

  // #469: the queue moved. Lay it over the same server map again; when a
  // write left it, read the server again (after a read already in flight,
  // which may have left before the write landed).
  const fetchRef = useRef(fetchAndStore);
  useEffect(() => {
    fetchRef.current = fetchAndStore;
  });
  const subscribe = opts.subscribe;
  useEffect(() => {
    if (!subscribe) return undefined;
    return subscribe((settled) => {
      if (serverRef.current) store(serverRef.current);
      if (!settled) return;
      lastFetchedRef.current = 0;
      const pending = inFlightRef.current;
      const again = () => void fetchRef.current();
      if (pending) void pending.then(again, again);
      else again();
    });
  }, [subscribe, store]);

  const getFresh = useCallback(async (): Promise<Map<string, number>> => {
    const ageMs = Date.now() - lastFetchedRef.current;
    if (lastFetchedRef.current > 0 && ageMs < MAXES_CACHE_TTL_MS) {
      return maxes;
    }
    return fetchAndStore();
  }, [maxes, fetchAndStore]);

  const getFreshDetailed = useCallback(async (): Promise<
    Map<string, CurrentMax>
  > => {
    await getFresh();
    return metaRef.current;
  }, [getFresh]);

  const forceRefresh = useCallback(async (): Promise<Map<string, number>> => {
    lastFetchedRef.current = 0;
    return fetchAndStore();
  }, [fetchAndStore]);

  return {
    maxes,
    maxMeta,
    loaded: status === "loaded",
    status,
    error,
    getFresh,
    getFreshDetailed,
    forceRefresh,
  };
}
